import React, { useState, useEffect, useMemo } from 'react';
import { Ban, X } from 'lucide-react';
import { composeColumnQuery } from 'composeColumnQuery';

const PropertyModal = ({ isOpen, onClose, onSearch, searchProperties, allProperties, isDark }) => {
  const [selected, setSelected] = useState(new Set(searchProperties));
  const [columnQueries, setColumnQueries] = useState({});
  const [negated, setNegated] = useState({});

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(searchProperties));
      setColumnQueries({});
      setNegated({});
    }
  }, [isOpen, searchProperties]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Composed from every row, in the order the columns are listed, so the
  // preview reads the same way the finished query will.
  const composedQuery = useMemo(
    () => composeColumnQuery(
      allProperties.map((prop) => ({
        token: prop.token,
        value: columnQueries[prop.name],
        negated: !!negated[prop.name],
      }))
    ),
    [allProperties, columnQueries, negated]
  );

  if (!isOpen) return null;

  const toggleProperty = (propName) => {
    const newSelected = new Set(selected);
    if (newSelected.has(propName)) {
      newSelected.delete(propName);
    } else {
      newSelected.add(propName);
    }
    setSelected(newSelected);
  };

  const toggleNegated = (propName) => {
    setNegated(prev => ({ ...prev, [propName]: !prev[propName] }));
  };

  // One confirm for both halves of the modal: the checkboxes commit as the
  // display columns exactly as Save Changes did, and the typed rows become
  // the main search bar's query.
  const saveAndSearch = () => {
    if (selected.size === 0) return;
    onSearch(composedQuery, Array.from(selected));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-night bg-opacity-70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col rounded-lg
        ${isDark ? 'bg-night-raised' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >

        {/* Header */}
        <div className={`p-4 border-b flex justify-between items-center
          ${isDark ? 'bg-night-header border-brand-900 text-brand-200' : 'bg-brand-50 border-brand-100 text-brand-900'}`}>
          <h2 className={`text-lg font-semibold
            ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Searchable Columns
          </h2>
          <button
            onClick={onClose}
            className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Checked columns are what the main search box matches against. Type below to
            build a search — the no-entry icon excludes matches instead of requiring them.
          </p>
          <div className="space-y-2">
            {allProperties.map((prop) => (
              <div
                key={prop.name}
                className={`flex items-center gap-2 p-2 rounded
                  ${isDark ? 'hover:bg-night-header' : 'hover:bg-brand-50'}`}
              >
                <label className="flex items-center gap-2 cursor-pointer w-36 shrink-0">
                  <input
                    type="checkbox"
                    checked={selected.has(prop.name)}
                    onChange={() => toggleProperty(prop.name)}
                    className={`h-4 w-4 rounded border accent-brand-500
                      ${isDark ? 'bg-night-raised border-brand-700' : 'border-brand-300'}`}
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {prop.label}
                  </span>
                </label>
                <input
                  type="text"
                  value={columnQueries[prop.name] || ''}
                  onChange={(e) => setColumnQueries(prev => ({ ...prev, [prop.name]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveAndSearch(); }}
                  placeholder={`Search ${prop.label}...`}
                  className={`flex-1 min-w-0 px-2 py-1 text-sm border rounded
                    ${isDark
                      ? 'bg-night-header border-brand-800 text-white placeholder-gray-500 focus:border-brand-500'
                      : 'bg-white border-brand-200 text-gray-900 placeholder-gray-400 focus:border-brand-500'}`}
                />
                <button
                  type="button"
                  onClick={() => toggleNegated(prop.name)}
                  aria-pressed={!!negated[prop.name]}
                  aria-label={`Exclude ${prop.label} matches`}
                  title={`Exclude ${prop.label} matches`}
                  className={`p-1.5 rounded shrink-0 transition-colors
                    ${negated[prop.name]
                      ? 'text-red-500 hover:text-red-400'
                      : isDark ? 'text-gray-600 hover:text-gray-500' : 'text-gray-300 hover:text-gray-400'}`}
                >
                  <Ban className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Live preview — the query being built, so the syntax is learned by
            watching it assemble rather than only from the Help modal. */}
        <div className={`px-4 py-2 border-t ${isDark ? 'border-brand-900' : 'border-brand-100'}`}>
          <div className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Search query
          </div>
          <code
            data-testid="composed-query-preview"
            className={`block text-sm font-mono break-all
              ${composedQuery
                ? (isDark ? 'text-brand-300' : 'text-brand-700')
                : (isDark ? 'text-gray-600' : 'text-gray-400')}`}
          >
            {composedQuery || 'Type in a column above to build a query'}
          </code>
        </div>

        {/* Footer */}
        <div className={`p-4 border-t flex justify-between items-center
          ${isDark ? 'border-brand-900' : 'border-brand-100'}`}>
          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {selected.size === 0 && 'Select at least one column'}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-lg
                ${isDark
                  ? 'border-brand-800 text-brand-200 hover:bg-night-header'
                  : 'border-brand-200 text-brand-800 hover:bg-brand-50'}`}
            >
              Cancel
            </button>
            <button
              onClick={saveAndSearch}
              disabled={selected.size === 0}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:bg-brand-300 disabled:cursor-not-allowed"
            >
              Save and Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyModal;
